/**
 * sessionSnapshot — persists the running question session so a deploy
 * reload, PWA kill, or crash can offer a resume instead of silently
 * losing the user's progress (audit F-09).
 *
 * Deliberately minimal: question IDs (not full objects), answers,
 * submitted flags, index and remaining exam seconds. Full question data
 * is rebuilt from the local question cache on resume.
 */
import logger from './logger';

export interface SessionSnapshot {
  v: 1;
  mode: 'learn' | 'exam' | 'mistakes' | 'srs' | 'bookmarks' | 'smart';
  chapterId?: number;
  isPreview?: boolean;
  questionIds: string[];
  answers: Record<number, string[]>;
  submitted: Record<number, boolean>;
  currentIndex: number;
  examTimeLeft: number;
  savedAt: number;
}

const KEY = 'fahryo_session_snapshot_v1';
// Older than this and the snapshot is silently dropped — resuming a
// days-old half-finished exam is more confusing than helpful.
const MAX_AGE_MS = 24 * 60 * 60 * 1000;

export function saveSessionSnapshot(snap: Omit<SessionSnapshot, 'v' | 'savedAt'>): void {
  try {
    const payload: SessionSnapshot = { ...snap, v: 1, savedAt: Date.now() };
    localStorage.setItem(KEY, JSON.stringify(payload));
  } catch {
    // Quota exceeded / private mode — resume is best-effort and must
    // never break the running session.
  }
}

export function loadSessionSnapshot(): SessionSnapshot | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const snap = JSON.parse(raw) as SessionSnapshot;
    if (
      snap?.v !== 1 ||
      !snap.mode ||
      !Array.isArray(snap.questionIds) ||
      snap.questionIds.length === 0 ||
      typeof snap.currentIndex !== 'number'
    ) {
      clearSessionSnapshot();
      return null;
    }
    if (Date.now() - (snap.savedAt || 0) > MAX_AGE_MS) {
      clearSessionSnapshot();
      return null;
    }
    return snap;
  } catch (e) {
    logger.warn('SessionSnapshot', 'Unreadable snapshot dropped');
    logger.debug('SessionSnapshot', 'Parse error:', e);
    clearSessionSnapshot();
    return null;
  }
}

export function clearSessionSnapshot(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
}
